import { TOptions } from 'i18next'

export type TranslationOptions = TOptions & {
    showKeys?: boolean
}

export interface TranslationOptionsSource {
    read: () => TranslationOptions
    subscribe?: (listener: () => void) => () => void
}

const noOptions: TranslationOptions = {}

let readCurrentOptions: () => TranslationOptions = () => noOptions
let unsubscribeFromSource: (() => void) | null = null
let lastKnownOptions: TranslationOptions = noOptions
let optionsVersion = 0

const listeners = new Set<() => void>()

const optionsDiffer = (previous: TranslationOptions, next: TranslationOptions): boolean => {
    const previousKeys = Object.keys(previous)
    const nextKeys = Object.keys(next)

    if (previousKeys.length !== nextKeys.length) {
        return true
    }

    return nextKeys.some((key) => previous[key] !== next[key])
}

const publishChange = (): void => {
    optionsVersion += 1
    listeners.forEach((listener) => listener())
}

const handleSourceChange = (): void => {
    const nextOptions = readCurrentOptions()

    if (!optionsDiffer(lastKnownOptions, nextOptions)) {
        return
    }

    lastKnownOptions = nextOptions
    publishChange()
}

const detachSource = (): void => {
    if (unsubscribeFromSource === null) {
        return
    }

    unsubscribeFromSource()
    unsubscribeFromSource = null
}

export const configureTranslations = (source: TranslationOptionsSource): void => {
    detachSource()

    readCurrentOptions = source.read
    lastKnownOptions = source.read()

    if (source.subscribe !== undefined) {
        unsubscribeFromSource = source.subscribe(handleSourceChange)
    }

    publishChange()
}

export const resetTranslations = (): void => {
    detachSource()

    readCurrentOptions = () => noOptions
    lastKnownOptions = noOptions

    publishChange()
}

export const readOptions = (): TranslationOptions => readCurrentOptions()

export const getOptionsVersion = (): number => optionsVersion

export const subscribeToOptions = (listener: () => void): (() => void) => {
    listeners.add(listener)

    return () => {
        listeners.delete(listener)
    }
}
